import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useState } from "react";
import { Activity, LogIn, AppWindow, Users as UsersIcon, Columns3, Download, FileText, X as XIcon, Search, Loader2, ChevronRight, ChevronUp, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { downloadFromUrl } from "@/lib/download";

const STAT_META = [
  { key: "events", label: "Total events", icon: Activity, tone: "text-primary" },
  { key: "logins", label: "Sign-ins", icon: LogIn, tone: "text-blue-500" },
  { key: "app_launches", label: "App launches", icon: AppWindow, tone: "text-teal-500" },
  { key: "active_users", label: "Active users", icon: UsersIcon, tone: "text-purple-500" },
];
const BAR_COLORS = ["#206bc4", "#4299e1", "#38b2ac", "#ed8936", "#9f7aea", "#ec4899", "#10b981", "#f59e0b"];

// Friendly labels for internal audit-log category codes.
const CATEGORY_LABELS = {
  app_access: "App launches",
  login: "Sign-ins",
  auth: "Authentication (MFA, masquerade, failed logins)",
  app: "App management",
  account: "Account changes",
  api_keys: "API keys",
  assistant: "AI Assistant (Ask AI)",
  clients: "Clients",
  developer: "Developer tools",
  did: "DID Master",
  inventory: "Inventory",
  organization: "Organizations & partners",
  permission: "Permissions & roles",
  projects: "Projects",
  quotes: "Quotes",
  request: "Access requests",
  settings: "Settings & branding",
  sip_trace: "SIP trace analysis",
  tasks: "Tasks",
  general: "General activity",
  activity_log: "Activity log exports",
};
const prettyCategory = (raw) => CATEGORY_LABELS[raw] || (raw || "Other").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Column defs per drill-down type. `always: true` = pinned/required (can't hide).
const COLS_EVENTS = [
  { id: "created_at", label: "When", always: true },
  { id: "actor_name", label: "Who", always: true },
  { id: "actor_role", label: "Role" },
  { id: "action", label: "Action" },
  { id: "category", label: "Category" },
  { id: "target_type", label: "Target type" },
  { id: "target_id", label: "Target" },
  { id: "details", label: "Details" },
  { id: "ip", label: "IP" },
];
const COLS_ACTIVE_USERS = [
  { id: "actor_name", label: "User", always: true },
  { id: "actor_role", label: "Role" },
  { id: "event_count", label: "Events" },
  { id: "first_seen", label: "First seen" },
  { id: "last_seen", label: "Last seen" },
  { id: "user_id", label: "User ID" },
];
const DEFAULT_VISIBLE = {
  events: ["created_at", "actor_name", "action", "category", "details"],
  logins: ["created_at", "actor_name", "action", "details", "ip"],
  app_launches: ["created_at", "actor_name", "action", "details", "ip"],
  active_users: ["actor_name", "actor_role", "event_count", "last_seen"],
};

const fmtDT = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

function DrillDown({ type, onClose }) {
  const meta = STAT_META.find((m) => m.key === type);
  const isUserAgg = type === "active_users";
  const cols = isUserAgg ? COLS_ACTIVE_USERS : COLS_EVENTS;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = usePersistedState(`analytics.${type}.search`, "");
  const [visibleIds, setVisibleIds] = useState(DEFAULT_VISIBLE[type] || []);
  const [exporting, setExporting] = useState(false);
  const prefKey = `analytics_${type}`;

  // Load user prefs
  useEffect(() => {
    api.get(`/me/table-prefs?table=${prefKey}`)
      .then((r) => {
        const saved = r.data?.prefs?.visible;
        if (Array.isArray(saved) && saved.length) setVisibleIds(saved);
        else setVisibleIds(DEFAULT_VISIBLE[type] || []);
      })
      .catch(() => setVisibleIds(DEFAULT_VISIBLE[type] || []));
  }, [prefKey, type]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/analytics/events", { params: { type, days: 14, search: search || undefined, limit: 500 } });
      setRows(data.rows || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  }, [type, search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const saveVisible = async (next) => {
    setVisibleIds(next);
    try { await api.put(`/me/table-prefs`, { table: prefKey, prefs: { visible: next } }); }
    catch { /* silent */ }
  };
  const toggleCol = (id) => {
    const col = cols.find((c) => c.id === id);
    if (col?.always) return;
    if (visibleIds.includes(id)) saveVisible(visibleIds.filter((v) => v !== id));
    else saveVisible([...visibleIds, id]);
  };
  const moveCol = (id, dir) => {
    const idx = visibleIds.indexOf(id);
    if (idx < 0) return;
    const next = [...visibleIds];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    saveVisible(next);
  };
  const resetCols = async () => {
    const dflt = DEFAULT_VISIBLE[type] || [];
    setVisibleIds(dflt);
    try { await api.put(`/me/table-prefs`, { table: prefKey, prefs: {} }); } catch { /* silent */ }
  };

  const exportView = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ type, days: "14", format, columns: visibleIds.join(",") });
      if (search) params.set("search", search);
      const { data } = await api.get(`/analytics/events/export?${params.toString()}`);
      if (!data?.token) throw new Error("No download token");
      await downloadFromUrl(`/files/${data.token}`);
      toast.success(`Exported ${data.count} row${data.count === 1 ? "" : "s"} as ${format.toUpperCase()}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed"); }
    finally { setExporting(false); }
  };

  const renderCell = (row, id) => {
    if (id === "created_at" || id === "last_seen" || id === "first_seen") return fmtDT(row[id]);
    if (id === "category") return prettyCategory(row.category);
    if (id === "event_count") return <span className="font-semibold text-slate-700">{row.event_count}</span>;
    return row[id] || "";
  };

  return (
    <Card className="mt-4 border-slate-200 shadow-sm" data-testid={`analytics-drilldown-${type}`}>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-800">{meta?.label} — last 14 days</h3>
          <p className="text-xs text-slate-400">
            {isUserAgg ? "Each row is one user, sorted by how active they've been." : "Each row is one audit event. Click Columns to choose which fields to show."}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="h-9 pl-8 w-52" data-testid={`analytics-search-${type}`} />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9" data-testid={`analytics-cols-${type}`}>
                <Columns3 className="h-3.5 w-3.5" /> Columns
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="end">
              <div className="mb-1 px-2 pt-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">Show &amp; reorder columns</div>
              <div className="space-y-0.5">
                {visibleIds.map((id, i) => {
                  const col = cols.find((c) => c.id === id); if (!col) return null;
                  const first = i === 0 || (i === 1 && cols.find((c) => c.id === visibleIds[0])?.always);
                  const last = i === visibleIds.length - 1;
                  return (
                    <div key={id} className="flex items-center gap-2 rounded px-2 py-1 hover:bg-slate-50">
                      <Checkbox checked disabled={col.always} onCheckedChange={() => toggleCol(id)} data-testid={`analytics-col-toggle-${type}-${id}`} />
                      <span className={`flex-1 text-sm ${col.always ? "text-slate-400" : "text-slate-700"}`}>{col.label}{col.always && <span className="ml-1 text-[10px] text-slate-300">(pinned)</span>}</span>
                      {!col.always && (
                        <>
                          <button onClick={() => moveCol(id, -1)} disabled={first} className="rounded p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`analytics-col-up-${type}-${id}`}><ChevronUp className="h-3.5 w-3.5" /></button>
                          <button onClick={() => moveCol(id, 1)} disabled={last} className="rounded p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`analytics-col-down-${type}-${id}`}><ChevronDown className="h-3.5 w-3.5" /></button>
                        </>
                      )}
                    </div>
                  );
                })}
                {cols.filter((c) => !c.always && !visibleIds.includes(c.id)).map((c) => (
                  <div key={c.id} className="flex items-center gap-2 rounded px-2 py-1 opacity-70 hover:bg-slate-50">
                    <Checkbox checked={false} onCheckedChange={() => toggleCol(c.id)} data-testid={`analytics-col-toggle-${type}-${c.id}`} />
                    <span className="flex-1 text-sm text-slate-500">{c.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 border-t border-slate-100 pt-1.5 text-center">
                <button onClick={resetCols} className="text-[11px] font-medium text-slate-400 hover:text-slate-700" data-testid={`analytics-col-reset-${type}`}>Reset to defaults</button>
              </div>
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="sm" className="h-9" disabled={exporting || rows.length === 0} onClick={() => exportView("csv")} data-testid={`analytics-export-csv-${type}`}>
            <Download className="h-3.5 w-3.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" className="h-9" disabled={exporting || rows.length === 0} onClick={() => exportView("pdf")} data-testid={`analytics-export-pdf-${type}`}>
            <FileText className="h-3.5 w-3.5" /> PDF
          </Button>
          <Button variant="ghost" size="sm" className="h-9 text-slate-400 hover:text-slate-700" onClick={onClose} data-testid={`analytics-drilldown-close-${type}`}><XIcon className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>{visibleIds.map((id) => <th key={id} className="px-4 py-2 font-semibold">{cols.find((c) => c.id === id)?.label || id}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={visibleIds.length} className="px-4 py-6 text-center text-slate-400"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={visibleIds.length} className="px-4 py-8 text-center text-slate-400">No events in the last 14 days.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={`${r.log_id || r.user_id || i}`} className="hover:bg-slate-50" data-testid={`analytics-row-${type}-${i}`}>
                {visibleIds.map((id) => <td key={id} className="px-4 py-2 text-slate-700">{renderCell(r, id)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-400">
        {rows.length} row{rows.length === 1 ? "" : "s"} shown. Older data isn&apos;t kept — analytics reflect the last 14 days.
      </div>
    </Card>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);  // active card key or null
  const [resetting, setResetting] = useState(false);
  const isSuper = (user?.role || user?.base_role) === "super_admin";

  const load = () => api.get("/analytics/overview", { params: { days: 14 } })
    .then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Counters only — the audit trail underneath is never touched, so this is
  // reversible and nothing is lost.
  const resetCounters = async () => {
    const typed = window.prompt('This zeroes the counters from now on. The audit trail is kept and you can undo it.\n\nType RESET to confirm:');
    if (!typed) return;
    setResetting(true);
    try {
      await api.post("/analytics/reset", { confirm: typed });
      toast.success("Counters reset — analytics now count from this moment");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reset the counters");
    } finally { setResetting(false); }
  };

  const undoReset = async () => {
    setResetting(true);
    try {
      await api.delete("/analytics/reset");
      toast.success("Full history is back");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't undo the reset");
    } finally { setResetting(false); }
  };

  if (data === null) return <div className="text-slate-400">Loading analytics…</div>;
  if (data === false) return <div className="text-slate-400">Unable to load analytics.</div>;

  const primary = getComputedStyle(document.documentElement).getPropertyValue("--primary") || "#206bc4";

  return (
    <div data-testid="analytics-page">
      <PageHeader testid="analytics-header" title="Usage Analytics"
        subtitle={data.baseline
          ? `Counting since ${new Date(data.baseline).toLocaleString()} — click any card to see the audit trail.`
          : "Platform activity across the last 14 days. Click any card to see the full audit trail."} />

      {isSuper && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button type="button" onClick={resetCounters} disabled={resetting}
            data-testid="analytics-reset"
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-rose-300 hover:text-rose-600 disabled:opacity-50">
            Reset counters to zero
          </button>
          {data.baseline && (
            <button type="button" onClick={undoReset} disabled={resetting}
              data-testid="analytics-reset-undo"
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-primary/40 hover:text-primary disabled:opacity-50">
              Show the full history again
            </button>
          )}
          <span className="text-[11px] text-slate-400">
            Counters only — the audit trail is kept either way.
          </span>
        </div>
      )}

      {data.scoped_to_me && (
        <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300"
          data-testid="analytics-scoped-note">
          Showing <b>your own activity only</b> — what you did, plus anything an admin did to your account.
          Platform-wide figures need the &ldquo;Activity logs — everyone&rsquo;s&rdquo; permission.
        </div>
      )}

      <div className={`grid grid-cols-2 gap-4 ${data.scoped_to_me ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}>
        {STAT_META.filter((m) => !(data.scoped_to_me && m.key === "active_users")).map((m) => {
          const Icon = m.icon;
          const active = drill === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setDrill(active ? null : m.key)}
              data-testid={`analytics-stat-${m.key}`}
              className={`group relative rounded-xl border p-5 text-left shadow-sm transition-all
                ${active ? "border-primary/50 bg-primary/5 shadow-md" : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{m.label}</span>
                <Icon className={`h-4 w-4 ${m.tone}`} />
              </div>
              <div className="mt-2 font-heading text-3xl font-bold text-slate-900">{data.totals[m.key] ?? 0}</div>
              <div className={`mt-2 flex items-center gap-1 text-[11px] font-medium ${active ? "text-primary" : "text-slate-400 group-hover:text-primary"}`}>
                {active ? "Hide details" : "View details"} <ChevronRight className="h-3 w-3" />
              </div>
            </button>
          );
        })}
      </div>

      {drill && <DrillDown type={drill} onClose={() => setDrill(null)} />}

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="border-slate-200 shadow-sm p-5 lg:col-span-2" data-testid="analytics-logins-chart">
          <h3 className="mb-4 font-heading text-sm font-semibold text-slate-800">Sign-ins per day</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.logins_by_day} margin={{ left: -20, right: 8, top: 4 }}>
              <defs>
                <linearGradient id="lg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={primary} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Area type="monotone" dataKey="logins" stroke={primary} strokeWidth={2} fill="url(#lg)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="border-slate-200 shadow-sm p-5" data-testid="analytics-category-chart">
          <h3 className="mb-1 font-heading text-sm font-semibold text-slate-800">Activity by category</h3>
          <p className="mb-3 text-xs text-slate-400">What kinds of things people did in the platform.</p>
          {data.category_breakdown.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">No activity yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.category_breakdown.map((c) => ({ ...c, label: prettyCategory(c.category) }))} layout="vertical" margin={{ left: 4, right: 8 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} cursor={{ fill: "#f1f5f9" }}
                  formatter={(v) => [v, "events"]} labelFormatter={(l) => l} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {data.category_breakdown.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div className={`mt-4 grid grid-cols-1 gap-4 ${data.scoped_to_me ? "" : "lg:grid-cols-2"}`}>
        {!data.scoped_to_me && (
        <Card className="border-slate-200 shadow-sm p-5" data-testid="analytics-top-users">
          <h3 className="mb-3 font-heading text-sm font-semibold text-slate-800">Most active users</h3>
          {data.top_users.length === 0 ? <div className="py-6 text-center text-sm text-slate-400">No data.</div> : (
            <div className="space-y-2">
              {data.top_users.map((u) => (
                <div key={u.name} className="flex items-center gap-3">
                  <span className="w-28 truncate text-sm text-slate-600">{u.name}</span>
                  <div className="h-2 flex-1 rounded-full bg-slate-100">
                    <div className="h-2 rounded-full bg-primary" style={{ width: `${(u.count / data.top_users[0].count) * 100}%` }} />
                  </div>
                  <span className="w-8 text-right text-xs font-medium text-slate-500">{u.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        )}
        <Card className="border-slate-200 shadow-sm p-5" data-testid="analytics-top-apps">
          <h3 className="mb-3 font-heading text-sm font-semibold text-slate-800">Most launched apps</h3>
          {data.top_apps.length === 0 ? <div className="py-6 text-center text-sm text-slate-400">No app launches yet.</div> : (
            <div className="space-y-2">
              {data.top_apps.map((a) => (
                <div key={a.name} className="flex items-center gap-3">
                  <span className="w-28 truncate text-sm text-slate-600">{a.name}</span>
                  <div className="h-2 flex-1 rounded-full bg-slate-100">
                    <div className="h-2 rounded-full bg-teal-500" style={{ width: `${(a.count / data.top_apps[0].count) * 100}%` }} />
                  </div>
                  <span className="w-8 text-right text-xs font-medium text-slate-500">{a.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

    </div>
  );
}



